'use client';

import Form from 'next/form';
import { FC, ReactNode, Ref, SubmitEvent, useActionState, useEffect, useTransition } from 'react';
import { shallow } from 'zustand/shallow';

import { RECAPTCHA_FIELD_NAME, SERVER_ACTION_NOT_SUBMITTED } from '@/constants/constants';
import { RecaptchaAction, getRecaptchaToken } from '@/general/helpers/getRecaptchaToken';
import { ActionResponse, ActionSuccess, ServerAction } from '@/general/helpers/serverActions';
import { useExternalServiceStore } from '@/stores/externalServiceStore';

type FormWithActionContent = <Schema = unknown, SuccessData extends object | string | undefined = undefined>(
  state: ActionResponse<Schema, SuccessData>,
) => ReactNode;

type Props<Schema = unknown, SuccessData extends object | string | undefined = undefined> = {
  action: ServerAction<Schema, SuccessData>;
  onSuccess: (success: ActionSuccess<SuccessData>) => void;
  onSubmit?: (data: FormData) => void;
  renderContent: FormWithActionContent;
  withRecaptcha?: RecaptchaAction;
  formRef?: Ref<HTMLFormElement>;
  dataTestId?: string;
};

export const getFormDataWithSubmitter = (form: HTMLFormElement, submitter?: HTMLElement | null): FormData => {
  const formData = new FormData(form);

  if ((submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) && submitter.name.length > 0) {
    formData.set(submitter.name, submitter.value);
  }

  return formData;
};

export const FormWithAction: FC<Props> = ({
  action,
  onSuccess,
  onSubmit,
  renderContent,
  withRecaptcha,
  formRef,
  dataTestId,
}) => {
  type FormState = NonNullable<Parameters<typeof action>[0]>;
  const initialState = SERVER_ACTION_NOT_SUBMITTED as FormState;

  const [state, formAction] = useActionState(async (prevState: FormState, formData: FormData) => {
    onSubmit?.(formData);

    // If recaptcha is enabled, add the token to the payload.
    if (withRecaptcha) {
      const token = await getRecaptchaToken(withRecaptcha);
      formData.append(RECAPTCHA_FIELD_NAME, token);
    }

    return action(prevState, formData);
  }, initialState);

  const [, startTransition] = useTransition();

  const { setShouldLoadRecaptcha } = useExternalServiceStore(
    ({ setShouldLoadRecaptcha }) => ({ setShouldLoadRecaptcha }),
    shallow,
  );

  useEffect(() => {
    if (state?.status === 'success') {
      onSuccess(state);
    }
  }, [state]);

  useEffect(() => {
    if (withRecaptcha) {
      setShouldLoadRecaptcha();
    }
  }, [withRecaptcha]);

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nativeEvent = event.nativeEvent;
    const formData = getFormDataWithSubmitter(event.currentTarget, nativeEvent.submitter);
    startTransition(() => {
      formAction(formData);
    });
  };

  return (
    <Form action={formAction} onSubmit={handleSubmit} noValidate ref={formRef} data-testid={dataTestId}>
      {renderContent(state)}
    </Form>
  );
};
